import { Link, useLocation, useParams } from 'react-router'
import { formatCompactNumber } from '@/lib/formatNumber'
import {
    chartPalette,
    CHART_GRID_STYLE,
    type ChartPalette,
} from '@/lib/chartColors'
import {
    Container,
    Stack,
    Group,
    Title,
    Text,
    Card,
    SimpleGrid,
    Badge,
    Button,
    Paper,
    Box,
    ThemeIcon,
    Divider,
    useComputedColorScheme,
} from '@mantine/core'
import {
    IconArrowLeft,
    IconUsers,
    IconChartHistogram,
    IconCoin,
    IconCalendarStats,
    IconRepeat,
    IconDownload,
} from '@tabler/icons-react'
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title as ChartTitle,
    Tooltip,
    Legend,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'
import { genericMemo } from '@/components/genericMemo'
import { MODELING_PRIORITY_LABEL } from '@/features/segment/labels'
import type { TransactionAudienceConfiguration } from '@/lib/domain'
import type { FC } from 'react'

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    ChartTitle,
    Tooltip,
    Legend
)

const chartOptionsFor = (pal: ChartPalette) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: { display: true, labels: { color: pal.tick } },
    },
    scales: {
        x: { ticks: { color: pal.tick }, grid: { color: pal.grid } },
        y: { ticks: { color: pal.tick }, grid: { color: pal.grid } },
    },
})

// The demographic consts hold labels + values only; inject the scheme-aware
// series colors at render. dataset[0] = Seed (secondary), [1] = Expanded (the
// brighter, more prominent series).
const withPalette = (
    data: {
        labels: (string | number)[]
        datasets: { label: string; data: number[] }[]
    },
    pal: ChartPalette
) => ({
    ...data,
    datasets: data.datasets.map((ds, i) => ({
        ...ds,
        backgroundColor: pal.series[i]?.fill,
        borderColor: pal.series[i]?.border,
        borderWidth: 1,
    })),
})

interface ExpansionState {
    seedAudience: TransactionAudienceConfiguration
    audienceSize: number | null
    lookalikePercentage: number
    selectedModel: 'balanced' | 'similarity' | 'reach'
}

const MODEL_MULTIPLIERS: Record<string, number> = {
    similarity: 0.7,
    balanced: 1.0,
    reach: 1.5,
}

const OVERLAP_RATES: Record<string, number> = {
    similarity: 0.28,
    balanced: 0.18,
    reach: 0.11,
}

// Mocked demographic data for the expanded audience
const EXPANDED_AGE_DATA = {
    labels: [18, 25, 30, 35, 40, 45, 50, 55, 60, 65],
    datasets: [
        {
            label: 'Seed Audience',
            data: [
                95_000, 280_000, 410_000, 520_000, 460_000, 350_000, 270_000,
                180_000, 120_000, 75_000,
            ],
        },
        {
            label: 'Expanded Audience',
            data: [
                210_000, 520_000, 680_000, 810_000, 740_000, 590_000, 430_000,
                310_000, 220_000, 150_000,
            ],
        },
    ],
}

const EXPANDED_METRO_DATA = {
    labels: [
        'New York, NY',
        'Los Angeles, CA',
        'Chicago, IL',
        'Dallas-Fort Worth, TX',
        'Houston, TX',
        'Atlanta, GA',
        'Philadelphia, PA',
        'Phoenix, AZ',
    ],
    datasets: [
        {
            label: 'Seed Audience',
            data: [
                380_000, 310_000, 245_000, 210_000, 185_000, 165_000, 140_000,
                125_000,
            ],
        },
        {
            label: 'Expanded Audience',
            data: [
                620_000, 530_000, 410_000, 380_000, 340_000, 295_000, 260_000,
                230_000,
            ],
        },
    ],
}

const EXPANDED_RUCA_DATA = {
    labels: ['1 - Urban', '4 - Suburban', '7 - Small Town', '10 - Rural'],
    datasets: [
        {
            label: 'Seed Audience',
            data: [1_200_000, 750_000, 350_000, 180_000],
        },
        {
            label: 'Expanded Audience',
            data: [2_100_000, 1_350_000, 680_000, 390_000],
        },
    ],
}

export const AudienceExpansionPage: FC = genericMemo(() => {
    const { threadId } = useParams<{ threadId: string }>()
    const location = useLocation()
    const state = location.state as ExpansionState | null
    const pal = chartPalette(useComputedColorScheme('light'))

    if (!state) {
        return (
            <Container size='xl' py='xl'>
                <Stack align='center' gap='md'>
                    <Text c='dimmed'>
                        No expansion data available. Please create an expansion
                        from an audience detail view.
                    </Text>
                    <Button
                        variant='light'
                        leftSection={<IconArrowLeft size={16} />}
                        component={Link}
                        to={`/thread/${threadId}`}
                    >
                        Back to Audience
                    </Button>
                </Stack>
            </Container>
        )
    }

    const { seedAudience, audienceSize, lookalikePercentage, selectedModel } =
        state

    const seedSize = audienceSize ?? 0
    const multiplier = MODEL_MULTIPLIERS[selectedModel] ?? 1
    const expandedSize = Math.round(seedSize * lookalikePercentage * multiplier)
    const overlapRate = OVERLAP_RATES[selectedModel] ?? 0.18

    return (
        <Container size='xl' py='xl'>
            <Stack gap='xl'>
                {/* Header */}
                <Box>
                    <Button
                        variant='subtle'
                        leftSection={<IconArrowLeft size={16} />}
                        mb='sm'
                        component={Link}
                        to={`/thread/${threadId}`}
                    >
                        Back to Audience
                    </Button>
                    <Title order={1} size='2rem' mb='xs'>
                        <IconUsers size='1.5rem' /> Audience Expansion:{' '}
                        {seedAudience.name || 'Transaction Segment'}
                    </Title>
                    <Text size='lg' c='dimmed'>
                        {MODELING_PRIORITY_LABEL[selectedModel]} model at{' '}
                        {lookalikePercentage}% similarity
                    </Text>
                </Box>

                {/* Summary Stats */}
                <SimpleGrid cols={{ base: 1, sm: 3 }} spacing='lg'>
                    <Card shadow='sm' padding='lg' radius='md' withBorder>
                        <Group gap='md'>
                            <ThemeIcon
                                size='xl'
                                radius='md'
                                variant='light'
                                color='blue'
                            >
                                <IconUsers size={24} />
                            </ThemeIcon>
                            <Box>
                                <Text
                                    size='xs'
                                    tt='uppercase'
                                    fw={700}
                                    c='dimmed'
                                >
                                    Seed Audience
                                </Text>
                                <Title order={2} size='h1'>
                                    {formatCompactNumber(seedSize)}
                                </Title>
                            </Box>
                        </Group>
                    </Card>

                    <Card shadow='sm' padding='lg' radius='md' withBorder>
                        <Group gap='md'>
                            <ThemeIcon
                                size='xl'
                                radius='md'
                                variant='light'
                                color='green'
                            >
                                <IconUsers size={24} />
                            </ThemeIcon>
                            <Box>
                                <Text
                                    size='xs'
                                    tt='uppercase'
                                    fw={700}
                                    c='dimmed'
                                >
                                    Expanded Audience
                                </Text>
                                <Title order={2} size='h1'>
                                    {formatCompactNumber(expandedSize)}
                                </Title>
                            </Box>
                        </Group>
                    </Card>

                    <Card shadow='sm' padding='lg' radius='md' withBorder>
                        <Group gap='md'>
                            <ThemeIcon
                                size='xl'
                                radius='md'
                                variant='light'
                                color='orange'
                            >
                                <IconChartHistogram size={24} />
                            </ThemeIcon>
                            <Box>
                                <Text
                                    size='xs'
                                    tt='uppercase'
                                    fw={700}
                                    c='dimmed'
                                >
                                    Audience Overlap
                                </Text>
                                <Title order={2} size='h1'>
                                    {Math.round(overlapRate * 100)}%
                                </Title>
                            </Box>
                        </Group>
                    </Card>
                </SimpleGrid>

                {/* Expansion Configuration */}
                <Card shadow='sm' padding='lg' radius='md' withBorder>
                    <Title order={3} mb='md'>
                        Expansion Configuration
                    </Title>
                    <SimpleGrid cols={{ base: 1, sm: 3 }} spacing='md'>
                        <Paper p='md' withBorder>
                            <Text size='xs' c='dimmed' tt='uppercase' mb='xs'>
                                Model Type
                            </Text>
                            <Badge size='lg' variant='light' color='blue'>
                                {MODELING_PRIORITY_LABEL[selectedModel]}
                            </Badge>
                        </Paper>
                        <Paper p='md' withBorder>
                            <Text size='xs' c='dimmed' tt='uppercase' mb='xs'>
                                Similarity Level
                            </Text>
                            <Badge size='lg' variant='light' color='violet'>
                                {lookalikePercentage}%
                            </Badge>
                        </Paper>
                        <Paper p='md' withBorder>
                            <Text size='xs' c='dimmed' tt='uppercase' mb='xs'>
                                Estimated Reach
                            </Text>
                            <Badge size='lg' variant='light' color='green'>
                                {formatCompactNumber(expandedSize)} users
                            </Badge>
                        </Paper>
                    </SimpleGrid>
                </Card>

                {/* RFM Breakdown */}
                <Card shadow='sm' padding='lg' radius='md' withBorder>
                    <Title order={3} mb='md'>
                        RFM Breakdown
                    </Title>
                    <SimpleGrid cols={{ base: 1, sm: 3 }} spacing='md'>
                        <Paper p='lg' withBorder>
                            <Group gap='md'>
                                <ThemeIcon
                                    size='lg'
                                    radius='md'
                                    variant='light'
                                    color='blue'
                                >
                                    <IconCalendarStats size={20} />
                                </ThemeIcon>
                                <Box>
                                    <Text size='xs' c='dimmed' tt='uppercase'>
                                        Recency
                                    </Text>
                                    <Title order={3}>7.2</Title>
                                    <Text size='xs' c='dimmed'>
                                        Avg days since last purchase: 18
                                    </Text>
                                </Box>
                            </Group>
                        </Paper>
                        <Paper p='lg' withBorder>
                            <Group gap='md'>
                                <ThemeIcon
                                    size='lg'
                                    radius='md'
                                    variant='light'
                                    color='orange'
                                >
                                    <IconRepeat size={20} />
                                </ThemeIcon>
                                <Box>
                                    <Text size='xs' c='dimmed' tt='uppercase'>
                                        Frequency
                                    </Text>
                                    <Title order={3}>4.8</Title>
                                    <Text size='xs' c='dimmed'>
                                        Avg purchases per quarter: 3.2
                                    </Text>
                                </Box>
                            </Group>
                        </Paper>
                        <Paper p='lg' withBorder>
                            <Group gap='md'>
                                <ThemeIcon
                                    size='lg'
                                    radius='md'
                                    variant='light'
                                    color='green'
                                >
                                    <IconCoin size={20} />
                                </ThemeIcon>
                                <Box>
                                    <Text size='xs' c='dimmed' tt='uppercase'>
                                        Monetary
                                    </Text>
                                    <Title order={3}>6.5</Title>
                                    <Text size='xs' c='dimmed'>
                                        Avg order value: $47.30
                                    </Text>
                                </Box>
                            </Group>
                        </Paper>
                    </SimpleGrid>
                </Card>

                {/* Demographic Comparison */}
                <Card shadow='sm' padding='lg' radius='md' withBorder>
                    <Title order={3} mb='md'>
                        Demographic Comparison
                    </Title>
                    <Box style={CHART_GRID_STYLE}>
                        <Card
                            withBorder
                            style={{
                                minHeight: '250px',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <Text size='sm' fw={500} mb='xs'>
                                Age Distribution
                            </Text>
                            <div style={{ flex: 1 }}>
                                <Bar
                                    options={chartOptionsFor(pal)}
                                    data={withPalette(EXPANDED_AGE_DATA, pal)}
                                />
                            </div>
                        </Card>

                        <Card
                            withBorder
                            style={{
                                minHeight: '250px',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <Text size='sm' fw={500} mb='xs'>
                                Top Metro Areas
                            </Text>
                            <div style={{ flex: 1 }}>
                                <Bar
                                    options={{
                                        ...chartOptionsFor(pal),
                                        indexAxis: 'y',
                                    }}
                                    data={withPalette(EXPANDED_METRO_DATA, pal)}
                                />
                            </div>
                        </Card>

                        <Card
                            withBorder
                            style={{
                                minHeight: '250px',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <Text size='sm' fw={500} mb='xs'>
                                Population Density
                            </Text>
                            <div style={{ flex: 1 }}>
                                <Bar
                                    options={{
                                        ...chartOptionsFor(pal),
                                        indexAxis: 'y',
                                    }}
                                    data={withPalette(EXPANDED_RUCA_DATA, pal)}
                                />
                            </div>
                        </Card>
                    </Box>
                </Card>

                <Divider />

                {/* Actions */}
                <Group justify='flex-end'>
                    <Button
                        variant='default'
                        leftSection={<IconArrowLeft size={16} />}
                        component={Link}
                        to={`/thread/${threadId}`}
                    >
                        Back to Audience
                    </Button>
                    <Button leftSection={<IconDownload size={16} />}>
                        Export Audience
                    </Button>
                </Group>
            </Stack>
        </Container>
    )
})
